import { useEffect, useRef, useState } from "react";

const Index = ({ widget }) => {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);
  const height = window.innerHeight;
  const width = window.innerWidth;

  const { data } = widget;
  const {
    url,
    videoId,
    controls = 1,
    loop = 0,
    bgColor = "#000000",
    zIndex = 1,
  } = data || {};

  // get autoplay from url query parameter
  const autoplay = new URLSearchParams(window.location.search).get("autoplay");

  const extractVideoId = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url);
      if (u.hostname === "youtu.be") return u.pathname.slice(1).split("/")[0];
      if (u.searchParams.get("v")) return u.searchParams.get("v");
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) =>
        ["live", "embed", "shorts", "v"].includes(p)
      );
      if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
      return null;
    } catch {
      return null;
    }
  };

  const finalVideoId = extractVideoId(url) || videoId;
  const isAutoplay = autoplay == 1;
  const shouldMute = isAutoplay ? 1 : 0;

  useEffect(() => {
    if (widget.data?.bgColor === "transparent") {
      document.body.style.backgroundColor = "transparent";
    } else if (bgColor) {
      document.body.style.backgroundColor = bgColor;
    }
  }, [widget.data, bgColor]);

  useEffect(() => {
    if (!finalVideoId) return;

    const loadYouTubeAPI = () => {
      if (window.YT && window.YT.Player) {
        initializePlayer();
        return;
      }

      if (!window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = initializePlayer;
      }

      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";
        script.async = true;
        document.body.appendChild(script);
      } else {
        setTimeout(() => {
          if (window.YT && window.YT.Player) {
            initializePlayer();
          }
        }, 500);
      }
    };

    const initializePlayer = () => {
      if (!containerRef.current || playerRef.current) return;

      // Autoplay videos stay muted (browsers block unmuted autoplay, and we
      // keep them muted by choice). Only when autoplay is OFF do we unmute once
      // playback begins — at that point a user gesture started it, so unmute()
      // is allowed.
      const unmuteOnce = (() => {
        let done = false;
        return (player) => {
          if (done || isAutoplay) return;
          try {
            player.unMute();
            player.setVolume(100);
            done = true;
          } catch (e) {}
        };
      })();

      playerRef.current = new window.YT.Player(containerRef.current, {
        height: "100%",
        width: "100%",
        videoId: finalVideoId,
        playerVars: {
          autoplay: isAutoplay ? 1 : 0,
          controls: controls ? 1 : 0,
          loop: loop ? 1 : 0,
          mute: shouldMute,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          enablejsapi: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: (event) => {
            setPlayerReady(true);
            const iframe = containerRef.current?.querySelector("iframe");
            if (iframe) {
              iframe.setAttribute("frameborder", "0");
              iframe.setAttribute("scrolling", "no");
              iframe.setAttribute(
                "allow",
                "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              );
              iframe.style.border = "none";
              iframe.style.width = `${width}px`;
              iframe.style.height = `${height}px`;
              iframe.style.zIndex = zIndex;
            }

            if (isAutoplay) {
              setTimeout(() => {
                if (event.target && event.target.playVideo) {
                  event.target.playVideo();
                }
              }, 300);
            }
          },
          onStateChange: (event) => {
            // YT.PlayerState.PLAYING === 1
            if (event.data === 1) unmuteOnce(event.target);
          },
        },
      });
    };

    loadYouTubeAPI();

    return () => {
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.log("Player destroy error:", e);
        }
        playerRef.current = null;
      }
    };
  }, [finalVideoId, isAutoplay, controls, loop, shouldMute]);

  if (!finalVideoId) {
    return (
      <div
        className="h-100 d-flex align-items-center justify-content-center"
        style={{ background: bgColor }}
      >
        <div style={{ color: "#fff" }}>No video ID provided</div>
      </div>
    );
  }

  return (
    <div
      className="h-100 w-100 d-flex align-items-center justify-content-center"
      style={{ background: bgColor }}
    >
      <div
        id="youtube-player-container"
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          maxWidth: width,
          maxHeight: height,
        }}
      />
    </div>
  );
};

export default Index;
